import {
  unsubscribeListQuery,
  useAddUnsubscribeMutation,
  useRemoveUnsubscribeMutation,
  type UnsubscribeEntry,
} from "@/api/campaigns";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { StyledLink } from "@/components/link/StyledLink";
import { Button } from "@/components/ui/Button";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { seo } from "@/utils/seo";
import { Box, Field, Flex, Label, Text, toast } from "@givenwell/components";
import { Input } from "@/components/ui/Input";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/campaigns/unsubscribed/")({
  component: UnsubscribedListPage,
  head: () => ({ meta: seo({ title: "Unsubscribed" }) }),
});

function UnsubscribedListPage() {
  const query = useQuery(unsubscribeListQuery());
  const addMut = useAddUnsubscribeMutation();
  const removeMut = useRemoveUnsubscribeMutation();

  const [showAdd, setShowAdd] = useState(false);
  const [memberId, setMemberId] = useState("");
  const [email, setEmail] = useState("");

  const entries = query.data || [];

  function handleAdd() {
    if (!memberId.trim() || !email.trim()) return;
    addMut.mutate(
      { member_id: memberId.trim(), email: email.trim() },
      {
        onSuccess: () => {
          toast.success("Added to unsubscribed list");
          setMemberId("");
          setEmail("");
          setShowAdd(false);
        },
      },
    );
  }

  return (
    <PageWrapper
      title="Unsubscribed"
      rightContent={
        <Button variant="tonal" color="primary" onClick={() => setShowAdd(!showAdd)}>
          Add Entry
        </Button>
      }
    >
      {showAdd && (
        <Box css={{ background: "white", p: 16, rounded: 12, mb: 16, spaceY: 12, maxWidth: 480 }}>
          <Text css={{ fontWeight: 600 }}>Add Unsubscribed Member</Text>
          <Field>
            <Label>Member ID</Label>
            <Input value={memberId} onValueChange={setMemberId} placeholder="memb_..." />
          </Field>
          <Field>
            <Label>Email</Label>
            <Input value={email} onValueChange={setEmail} placeholder="user@example.com" />
          </Field>
          <Flex css={{ gap: 8 }}>
            <Button variant="plain" color="neutral" onClick={() => setShowAdd(false)}>
              Cancel
            </Button>
            <Button
              variant="tonal"
              color="primary"
              onClick={handleAdd}
              loading={addMut.isPending}
              loadingText="Adding..."
              disabled={!memberId.trim() || !email.trim()}
            >
              Add
            </Button>
          </Flex>
        </Box>
      )}

      <DataTable>
        <DataTableHeader hideToggle />
        <DataTableBody
          data={entries}
          status={query.status}
          dataKey="member_id"
          emptyStateMessage="No unsubscribed members"
          columns={[
            {
              header: "Email",
              cell: (e: UnsubscribeEntry) => (
                <StyledLink to="/admin/members/$memberId" params={{ memberId: e.member_id }}>
                  {e.email}
                </StyledLink>
              ),
            },
            {
              header: "Unsubscribed From",
              cell: (e: UnsubscribeEntry) =>
                e.email_send_id ? (
                  <StyledLink to="/campaigns/sends/$sendId" params={{ sendId: e.email_send_id }}>
                    {e.email_send_name ?? e.email_send_id}
                  </StyledLink>
                ) : (
                  <Text css={{ fontScale: "sm", color: colors.text2 }}>Manual</Text>
                ),
            },
            {
              header: "Date",
              cell: (e: UnsubscribeEntry) => new Date(e.created_at).toLocaleString(),
            },
            {
              header: "",
              css: { align: "right" },
              cell: (e: UnsubscribeEntry) => (
                <Button
                  variant="plain"
                  color="red"
                  size="sm"
                  onClick={() =>
                    removeMut.mutate(e.member_id, {
                      onSuccess: () => toast.success("Removed from unsubscribed list"),
                    })
                  }
                >
                  Remove
                </Button>
              ),
            },
          ]}
        />
      </DataTable>
    </PageWrapper>
  );
}
